import { PCCase, ValidationError, ValidationResult } from '../types';
import { pcCases } from '../data/cases';
import { getUserCases } from './userCasesService';

interface ValidationRule {
  field: string;
  validate: (value: unknown, data: Partial<PCCase>) => string | null;
}

const CURRENT_YEAR = new Date().getFullYear();

/**
 * Validation rules for PC case fields
 */
const validationRules: ValidationRule[] = [
  // Required string fields
  {
    field: 'name',
    validate: (value) => {
      if (!value || (typeof value === 'string' && !value.trim())) {
        return 'Case name is required';
      }
      return null;
    },
  },
  {
    field: 'manufacturer',
    validate: (value) => {
      if (!value || (typeof value === 'string' && !value.trim())) {
        return 'Manufacturer is required';
      }
      return null;
    },
  },

  // Size category
  {
    field: 'sizeCategory',
    validate: (value) => {
      const validSizes = ['Full-Tower', 'Mid-Tower', 'Mini-Tower', 'SFF', 'HTPC'];
      if (!value || !validSizes.includes(value as string)) {
        return 'Please select a valid size category';
      }
      return null;
    },
  },

  // Dimensions
  {
    field: 'dimensions.height',
    validate: (_, data) => {
      const height = data.dimensions?.height;
      if (!height || height <= 0) {
        return 'Height is required';
      }
      if (height < 100 || height > 800) {
        return 'Height must be between 100mm and 800mm';
      }
      return null;
    },
  },
  {
    field: 'dimensions.width',
    validate: (_, data) => {
      const width = data.dimensions?.width;
      if (!width || width <= 0) {
        return 'Width is required';
      }
      if (width < 100 || width > 400) {
        return 'Width must be between 100mm and 400mm';
      }
      return null;
    },
  },
  {
    field: 'dimensions.depth',
    validate: (_, data) => {
      const depth = data.dimensions?.depth;
      if (!depth || depth <= 0) {
        return 'Depth is required';
      }
      if (depth < 100 || depth > 700) {
        return 'Depth must be between 100mm and 700mm';
      }
      return null;
    },
  },

  // GPU length
  {
    field: 'maxGpuLength',
    validate: (value) => {
      if (!value || (value as number) <= 0) {
        return 'Maximum GPU length is required';
      }
      const num = value as number;
      if (num < 100 || num > 600) {
        return 'GPU length must be between 100mm and 600mm';
      }
      return null;
    },
  },

  // CPU cooler height
  {
    field: 'maxCpuCoolerHeight',
    validate: (value) => {
      if (!value || (value as number) <= 0) {
        return 'Maximum CPU cooler height is required';
      }
      const num = value as number;
      if (num < 30 || num > 250) {
        return 'CPU cooler height must be between 30mm and 250mm';
      }
      return null;
    },
  },

  // PSU length (optional but validated if provided)
  {
    field: 'maxPsuLength',
    validate: (value) => {
      if (value && (value as number) > 0) {
        const num = value as number;
        if (num < 100 || num > 300) {
          return 'PSU length must be between 100mm and 300mm';
        }
      }
      return null;
    },
  },

  // Release year (optional but validated if provided)
  {
    field: 'releaseYear',
    validate: (value) => {
      if (value && (value as number) > 0) {
        const num = value as number;
        if (num < 2010 || num > CURRENT_YEAR) {
          return `Release year must be between 2010 and ${CURRENT_YEAR}`;
        }
      }
      return null;
    },
  },

  // Form factor support
  {
    field: 'formFactorSupport',
    validate: (value) => {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return 'At least one form factor must be supported';
      }
      const validFormFactors = ['E-ATX', 'ATX', 'Micro-ATX', 'Mini-ITX', 'Mini-DTX'];
      const allValid = value.every((ff) => validFormFactors.includes(ff as string));
      if (!allValid) {
        return 'Invalid form factor selected';
      }
      return null;
    },
  },
];

/**
 * Get a nested value from an object using dot notation
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((current, key) => {
    if (current && typeof current === 'object') {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj as unknown);
}

/**
 * Validate a partial case object
 */
export function validateCase(data: Partial<PCCase>): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: string[] = [];

  // Run all validation rules
  for (const rule of validationRules) {
    const value = getNestedValue(data as Record<string, unknown>, rule.field);
    const error = rule.validate(value, data);

    if (error) {
      errors.push({
        field: rule.field,
        message: error,
        type: error.includes('required') ? 'required' : 'range',
      });
    }
  }

  // Check for duplicate
  const duplicateCase = findDuplicateCase(data);
  if (duplicateCase) {
    errors.push({
      field: 'name',
      message: `A case with this name and manufacturer already exists: ${duplicateCase.name}`,
      type: 'duplicate',
    });
  }

  // Add warnings for suspicious values
  if (data.dimensions) {
    const volume =
      (data.dimensions.height * data.dimensions.width * data.dimensions.depth) /
      1000000;
    if (volume > 100) {
      warnings.push('The calculated volume seems unusually large. Please verify dimensions.');
    }
    if (volume < 5 && data.sizeCategory !== 'SFF') {
      warnings.push(
        'The calculated volume seems small for this size category. Please verify dimensions.'
      );
    }
  }

  if (data.maxGpuLength && data.dimensions?.depth) {
    if (data.maxGpuLength > data.dimensions.depth) {
      warnings.push(
        'Maximum GPU length exceeds case depth. Please verify these values.'
      );
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    duplicateCase,
  };
}

/**
 * Check if a case with the same name and manufacturer already exists
 */
function findDuplicateCase(data: Partial<PCCase>): PCCase | undefined {
  if (!data.name || !data.manufacturer) {
    return undefined;
  }

  const normalizedName = data.name.toLowerCase().trim();
  const normalizedMfr = data.manufacturer.toLowerCase().trim();

  // Check built-in cases
  const builtInDuplicate = pcCases.find(
    (c: PCCase) =>
      c.name.toLowerCase().trim() === normalizedName &&
      c.manufacturer.toLowerCase().trim() === normalizedMfr
  );

  if (builtInDuplicate) {
    return builtInDuplicate;
  }

  // Check user-submitted cases
  const userCases = getUserCases();
  const userDuplicate = userCases.find(
    (c) =>
      c.name.toLowerCase().trim() === normalizedName &&
      c.manufacturer.toLowerCase().trim() === normalizedMfr
  );

  return userDuplicate;
}

/**
 * Validate a single field
 */
export function validateField(
  field: string,
  value: unknown,
  data: Partial<PCCase>
): string | null {
  const rule = validationRules.find((r) => r.field === field);
  if (!rule) {
    return null;
  }
  return rule.validate(value, data);
}

/**
 * Check if all required fields have values (for enabling save button)
 */
export function hasRequiredFields(data: Partial<PCCase>): boolean {
  const requiredFields = [
    'name',
    'manufacturer',
    'sizeCategory',
    'dimensions',
    'maxGpuLength',
    'maxCpuCoolerHeight',
    'formFactorSupport',
  ];

  for (const field of requiredFields) {
    const value = getNestedValue(data as Record<string, unknown>, field);
    if (!value) return false;
    if (field === 'dimensions') {
      const dims = value as { height?: number; width?: number; depth?: number };
      if (!dims.height || !dims.width || !dims.depth) return false;
    }
    if (field === 'formFactorSupport' && Array.isArray(value) && value.length === 0) {
      return false;
    }
  }

  return true;
}
